import React, { useEffect, useState } from 'react';
import { api, adminHeaders, formatGBP, formatDate } from '@/lib/api';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';

const STATUS_COLORS = {
    pending: 'bg-[color:var(--paper-2)] text-[color:var(--graphite)] border border-[color:var(--border)]',
    in_progress: 'bg-[rgba(126,155,134,0.18)] text-[color:var(--umber)] border border-[rgba(126,155,134,0.35)]',
    completed: 'bg-[rgba(47,125,90,0.18)] text-[color:var(--umber)] border border-[rgba(47,125,90,0.35)]',
    shipped: 'bg-[rgba(43,108,176,0.18)] text-[color:var(--umber)] border border-[rgba(43,108,176,0.35)]',
};

const STATUSES = ['all', 'pending', 'in_progress', 'completed', 'shipped'];

export default function AdminOrders() {
    const [orders, setOrders] = useState(null);
    const [filter, setFilter] = useState('all');

    async function load() {
        const params = filter === 'all' ? {} : { params: { status: filter } };
        const r = await api.get('/admin/orders', { headers: adminHeaders(), ...params });
        setOrders(r.data);
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="caption">Orders</p>
                    <h1 className="font-display text-3xl text-[color:var(--umber)] mt-1">All commissions</h1>
                </div>
                <div className="w-48">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger data-testid="admin-orders-filter" className="bg-[color:var(--paper-2)] border-[color:var(--border)]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] overflow-hidden">
                <Table data-testid="admin-orders-table">
                    <TableHeader>
                        <TableRow className="border-[color:var(--border)]">
                            <TableHead>Order</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Payment</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="text-right">Open</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {orders === null && (
                            <TableRow><TableCell colSpan={7} className="text-center py-10 text-[color:var(--muted-fg)]" data-testid="loading-state">Loading…</TableCell></TableRow>
                        )}
                        {orders && orders.length === 0 && (
                            <TableRow><TableCell colSpan={7} className="text-center py-10 text-[color:var(--muted-fg)]" data-testid="empty-state">No orders found.</TableCell></TableRow>
                        )}
                        {orders && orders.map((o) => (
                            <TableRow key={o.id} className="border-[color:var(--border)] hover:bg-[color:var(--paper)] transition-colors" data-testid="admin-orders-row">
                                <TableCell className="font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</TableCell>
                                <TableCell>{o.customer?.name || <span className="text-[color:var(--muted-fg)]">—</span>}<div className="text-xs text-[color:var(--muted-fg)]">{o.customer?.email}</div></TableCell>
                                <TableCell>{formatGBP(o.total)}</TableCell>
                                <TableCell><Badge className={o.payment_status === 'paid' ? 'bg-[rgba(47,125,90,0.18)] text-[color:var(--umber)] border border-[rgba(47,125,90,0.35)]' : 'bg-[color:var(--paper)] text-[color:var(--graphite)] border border-[color:var(--border)]'}>{o.payment_status}</Badge></TableCell>
                                <TableCell><Badge className={STATUS_COLORS[o.status]}>{o.status.replace('_', ' ')}</Badge></TableCell>
                                <TableCell className="text-xs text-[color:var(--muted-fg)]">{formatDate(o.created_at)}</TableCell>
                                <TableCell className="text-right">
                                    <Link to={`/admin/orders/${o.id}`} data-testid={`admin-order-open-${o.id}`} className="under-link text-sm text-[color:var(--umber)]">Open</Link>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
